/**
 * PDF importer: a rota PDF -> *candidate* rows for the review table.
 *
 * **This module never applies anything.** `extractRotaFromPdf` returns data and
 * only data: a Rota the manager has not accepted yet, the issues we hit, a
 * confidence score and the raw grid we read. The import dialog puts that into
 * the editable review table and nothing reaches the store until a human presses
 * Apply. That rule exists because a PDF is the least trustworthy input we take - 
 * there are no cells in a PDF, only glyphs at coordinates, and every column we
 * report is an inference we made from x positions.
 *
 * The inference is the whole job. The source document is a rota table: a header
 * row of day columns ("Mon 27 Jul" … "Sun 02 Aug", then "Total hours
 * (Incl.breaks)"), a leading name column, and cells that are blank, `ABSENT` or
 * a time range. pdf.js hands that back as a flat, unordered bag of text items,
 * and it very frequently breaks one cell across two lines - "08:30 -" on one,
 * "14:30" on the next - so a naive reader loses half of every wrapped shift.
 * {@link textItemsToGrid} rebuilds the table from the geometry and stitches
 * those fragments back together; it is deliberately pure so the fiddly part can
 * be unit-tested with hand-written coordinates and no PDF at all.
 *
 * Once the grid exists, interpretation is delegated wholesale to
 * {@link rowsToRota} in `rotaParser.js`. Header detection, finding the name
 * column, dropping the weekly-total column and canonicalising cells must behave
 * identically whether the manager pasted a spreadsheet or dropped a PDF, and the
 * only way to guarantee that is to run the same code.
 *
 * `issues` are the same {@link ParseIssue} objects the paste importer produces - 
 * machine-readable, per cell, for the review table to render inline. `warnings`
 * are whole sentences aimed at the manager about the import as a whole: which
 * rows to look at, which week the document thinks it is, why nothing could be
 * read at all.
 *
 * @typedef {{ str: string, x: number, y: number, width: number, height: number }} TextItem
 * @typedef {{ row: number|null, column: string|null, value: string, code: string,
 *             message: string, severity: 'error'|'warning'|'info' }} ParseIssue
 * @typedef {{ rota: object, issues: ParseIssue[], confidence: number, pages: number,
 *             rawRows: string[][], warnings: string[],
 *             detectedWeekCommencing: string|null }} PdfImportResult
 */

import {
  DAY_KEYS,
  DAY_LABELS,
  classifyShiftCell,
  mondayOf,
  parseIsoDate,
} from '../core/time.js';
import { normaliseName } from '../core/ids.js';
import { rowsToRota } from './rotaParser.js';

/* -------------------------------------------------------------------------- */
/* Tuning constants                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Row clustering tolerance, as a share of the median glyph height. Half a line
 * of text is wide enough to absorb the sub-pixel baseline jitter inside one
 * table row and narrow enough never to swallow the next one.
 */
const ROW_TOLERANCE_HEIGHT_RATIO = 0.5;

/** Absolute floor for the tolerance, for PDFs that report tiny glyph heights. */
const MIN_ROW_TOLERANCE = 3;

/** Used when a PDF reports no usable glyph heights at all. */
const FALLBACK_ITEM_HEIGHT = 8;

/**
 * Two header items closer together than this (times the median glyph height)
 * are one wrapped label rather than two columns - "Mon 27" + "Jul" must not
 * produce two column boundaries.
 */
const HEADER_FRAGMENT_GAP_RATIO = 0.6;

/** Nearby baselines that together make one wrapped/vertically-centred header. */
const HEADER_REGION_HEIGHT_RATIO = 2.25;

/**
 * A second line belonging to the same table row sits closer to the first than a
 * genuine new row does. Anything under this share of the median row pitch is
 * treated as a wrap rather than a new person.
 */
const WRAPPED_LINE_GAP_RATIO = 0.75;

/**
 * A gap smaller than this many glyph heights is within-cell leading (a wrapped
 * line), not the step from one table row to the next. Used to keep the row-pitch
 * estimate from being dragged down by wrapped lines.
 */
const WRAPPED_LEADING_HEIGHT_RATIO = 1.6;

/**
 * Two name baselines this close are the two lines of one wrapped employee name.
 * The source export uses 18pt leading for wrapped names against a roughly 34pt
 * minimum table-row pitch, so this stays safely below the next real employee.
 */
const WRAPPED_NAME_GAP_HEIGHT_RATIO = 1.7;

/** Day names a row must contain before we believe it is the header row. */
const MIN_HEADER_DAY_MATCHES = 3;

/** Rows scoring below this are called out by name in `warnings`. */
const LOW_CONFIDENCE_THRESHOLD = 0.7;

/**
 * What each defect costs a row's confidence. A missing name is close to fatal
 * (the row cannot be matched to an employee at all); an unreadable cell costs a
 * quarter because the rest of the row is still useful; a stitched cell costs
 * only a little because we usually get it right but the manager should look.
 */
const PENALTIES = Object.freeze({
  MISSING_NAME: 0.4,
  UNPARSED_CELL: 0.25,
  STITCHED_CELL: 0.1,
});

/** Guard against a pathological upload locking up the browser tab. */
const MAX_PAGES = 20;

/** Day-of-week tokens, tolerant of the abbreviations rota exports use. */
const DAY_HEADER_PATTERNS = [
  { dayKey: 'mon', re: /\bmon(?:day)?\b/i },
  { dayKey: 'tue', re: /\btue(?:s|sday)?\b/i },
  { dayKey: 'wed', re: /\bwed(?:s|nesday)?\b/i },
  { dayKey: 'thu', re: /\bthu(?:r|rs|rsday)?\b/i },
  { dayKey: 'fri', re: /\bfri(?:day)?\b/i },
  { dayKey: 'sat', re: /\bsat(?:urday)?\b/i },
  { dayKey: 'sun', re: /\bsun(?:day)?\b/i },
];

const MONTH_SHORT = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

/** "WC 27 July 2026", "W/C 27th July", "Week commencing 27/07/2026". */
const WEEK_COMMENCING_RE = /\b(?:w[./\s]{0,2}c|week\s+(?:commencing|beginning)|wk\s+commencing)\b[^\n]{0,48}/i;

/** Page furniture that would otherwise be imported as a member of staff. */
const PAGE_FURNITURE_RE = /^page\s*\d+(?:\s*(?:of|\/)\s*\d+)?$/i;

/** Header wording that marks a summary column rather than a day. */
const TOTALS_HEADER_RE = /\b(?:total|totals|hours|hrs|breaks?)\b/i;

/** Every dash pdf.js might leave dangling at the end of a wrapped time range. */
const LEADING_DASH_RE = /^[-‐‑‒–\u2014―−]\s*/;
const TRAILING_DASH_RE = /\s*[-‐‑‒–\u2014―−]$/;

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Rebuild a table from a page's positioned text items.
 *
 * Kept pure - no pdf.js, no DOM, no clock - because this is where every
 * interesting mistake happens and it needs to be testable from a literal array
 * of coordinates. The steps are, in order:
 *
 *  1. cluster items into lines by `y`, with a tolerance derived from the median
 *     glyph height so the algorithm scales with the document's font size;
 *  2. pick the header line - the one naming the most days of the week;
 *  3. turn the header's item centres into column boundaries (midpoints between
 *     consecutive centres) and mirror the first gap leftwards to open a leading
 *     name column, because the names sit left of every dated header;
 *  4. drop each item into a column by its own centre;
 *  5. join same-cell items with a space;
 *  6. merge continuation lines into the row above, which is what puts "08:30 -"
 *     and "14:30" back into one cell, then tidy any dash left dangling.
 *
 * @param {TextItem[]} items Positioned text, in any order. `y` follows the PDF
 *   convention (larger is higher up the page) unless `yIncreasesDownward`.
 * @param {{ rowTolerance?: number, yIncreasesDownward?: boolean,
 *           mergeContinuations?: boolean }} [options]
 * @returns {string[][]} One array of cell strings per table row, top to bottom,
 *   column 0 being the name column. Never mutates `items`.
 */
export function textItemsToGrid(items, options = {}) {
  return buildGrid(items, options).rows.map((row) => row.cells.slice());
}

/**
 * Read a rota PDF and return everything the review table needs to show it.
 *
 * pdf.js is imported lazily so that its ~1 MB of code never enters the main
 * bundle for the many sessions that only paste text, and defensively, because
 * the module shape differs between bundlers (named exports here, a default
 * namespace under some CJS interop). A PDF we cannot read is a routine event,
 * not an exception: an encrypted, corrupt or scanned document comes back as a
 * confidence-0 result carrying an explanatory warning, so the dialog can tell
 * the manager what to do instead of surfacing a stack trace.
 *
 * The week-commencing date found in the document is reported as
 * `detectedWeekCommencing` and **never** applied. Silently re-dating a rota is
 * the one import mistake a manager would not notice until the wrong week's
 * lunches went out, so the choice stays with them.
 *
 * @param {File|Blob|ArrayBuffer|ArrayBufferView} file The uploaded PDF.
 * @param {{ weekCommencing?: string }} [options] ISO Monday the manager is
 *   importing into. Used for the rota itself and to date-match day headers.
 * @returns {Promise<PdfImportResult>} Resolves even for unreadable input.
 */
export async function extractRotaFromPdf(file, { weekCommencing = '' } = {}) {
  let data;
  try {
    data = await toPdfBytes(file);
  } catch (error) {
    return failureResult(weekCommencing, `Could not read the uploaded file: ${messageOf(error)}`);
  }

  let pdfjs;
  try {
    pdfjs = await loadPdfJs();
  } catch (error) {
    return failureResult(
      weekCommencing,
      `The PDF engine could not be loaded (${messageOf(error)}). Paste the rota as text instead.`,
    );
  }

  const warnings = [];
  const pageGrids = [];
  const lineTexts = [];
  let pages = 0;
  let glyphCount = 0;
  let pdfDocument = null;

  try {
    pdfDocument = await pdfjs.getDocument({ data, isEvalSupported: false }).promise;
    pages = Math.min(pdfDocument.numPages, MAX_PAGES);
    if (pdfDocument.numPages > MAX_PAGES) {
      warnings.push(
        `Only the first ${MAX_PAGES} of ${pdfDocument.numPages} pages were read. Split the PDF if the rota continues past that.`,
      );
    }

    for (let pageNumber = 1; pageNumber <= pages; pageNumber += 1) {
      const page = await pdfDocument.getPage(pageNumber);
      const content = await page.getTextContent();
      const items = (content?.items ?? [])
        .filter((item) => typeof item?.str === 'string')
        .map(toTextItem);
      glyphCount += items.filter((item) => item.str.trim() !== '').length;
      const grid = buildGrid(items);
      pageGrids.push(grid);
      lineTexts.push(...grid.rows.map((row) => row.cells.filter(Boolean).join(' ')));
      page.cleanup?.();
    }
  } catch (error) {
    return failureResult(weekCommencing, describePdfError(error), { pages });
  } finally {
    try {
      await pdfDocument?.destroy?.();
    } catch (error) {
      console.warn('Lunchly: releasing the PDF failed.', error);
    }
  }

  if (glyphCount === 0) {
    return failureResult(
      weekCommencing,
      'This PDF has no extractable text layer - it is a scan or an image of a rota. '
        + 'Re-export it from the rota system as a text PDF, or paste the table instead.',
      { pages },
    );
  }

  const headerCells = firstHeaderCells(pageGrids);
  if (!headerCells) {
    warnings.push(
      'No row of day names was found, so the columns were guessed from their position. Check every day before applying.',
    );
  }

  const bodyRows = collectBodyRows(pageGrids, headerCells);
  // The grid exactly as handed to the parser, so the review table can show what
  // was read rather than only what was understood. Copied because it is handed
  // to the caller as well.
  const rawRows = (headerCells ? [headerCells, ...bodyRows.map((row) => row.cells)] : bodyRows.map((row) => row.cells))
    .map((cells) => cells.slice());

  const detectedWeekCommencing = detectWeekCommencing(lineTexts.join('\n'), headerCells, weekCommencing);
  warnings.push(...weekCommencingWarnings(detectedWeekCommencing, weekCommencing));

  if (!bodyRows.length) {
    return failureResult(
      weekCommencing,
      'Text was found but no rota rows could be made out of it. Check that this is the rota table and not a cover page.',
      { pages, rawRows, detectedWeekCommencing, warnings },
    );
  }

  const parsed = rowsToRota(rawRows, { weekCommencing, source: 'pdf' });

  const issues = [...parsed.issues];
  const sourceRows = alignSourceRows(parsed.rota.rows, bodyRows);

  let weightedTotal = 0;
  let weightTotal = 0;
  parsed.rota.rows.forEach((rotaRow, index) => {
    const scored = scoreRotaRow({
      rotaRow,
      sourceRow: sourceRows[index],
      rowIndex: index,
      headerCells,
      issues,
    });
    weightedTotal += scored.confidence * scored.weight;
    weightTotal += scored.weight;
    if (scored.confidence < LOW_CONFIDENCE_THRESHOLD) {
      const label = rotaRow.name || `Row ${index + 1}`;
      const problems = scored.problems.length ? scored.problems.join('; ') : 'the row could not be read reliably';
      warnings.push(`${label}: ${problems}. Check this row before applying.`);
    }
  });

  return {
    rota: parsed.rota,
    issues,
    confidence: weightTotal > 0 ? round3(weightedTotal / weightTotal) : 0,
    pages,
    rawRows,
    warnings,
    detectedWeekCommencing,
  };
}

/* -------------------------------------------------------------------------- */
/* Grid construction (pure)                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The working form behind {@link textItemsToGrid}: the same grid plus the
 * geometry-derived facts the importer needs but the public contract does not
 * expose (which line was the header, which cells had to be stitched).
 */
function buildGrid(items, { rowTolerance, yIncreasesDownward = false, mergeContinuations = true } = {}) {
  const prepared = prepareItems(items);
  if (!prepared.length) return { rows: [], headerIndex: -1, medianHeight: 0 };

  const medianHeight = median(prepared.map((item) => item.height)) || FALLBACK_ITEM_HEIGHT;
  const tolerance = Number.isFinite(rowTolerance) && rowTolerance > 0
    ? rowTolerance
    : Math.max(MIN_ROW_TOLERANCE, medianHeight * ROW_TOLERANCE_HEIGHT_RATIO);

  const lines = clusterLines(prepared, tolerance, yIncreasesDownward);
  const headerLine = findHeaderLine(lines);
  // With no header to measure, the widest line is the best available guide to
  // where the columns sit - it is nearly always a fully populated data row.
  const guideLine = headerLine >= 0
    ? compositeHeaderLine(lines, headerLine, medianHeight)
    : widestLine(lines);
  const boundaries = columnBoundaries(guideLine, medianHeight);

  const rows = dropEmptyLeadingColumns(lines.map((line) => lineToRow(line, boundaries)));
  const merged = mergeContinuations
    ? mergeContinuationRows(rows, headerLine, medianHeight)
    : { rows, headerIndex: headerLine };

  for (const row of merged.rows) tidyRow(row);

  return { rows: merged.rows, headerIndex: merged.headerIndex, medianHeight };
}

/** Drop empty/degenerate items and pre-compute the horizontal centre. */
function prepareItems(items) {
  const prepared = [];
  for (const item of Array.isArray(items) ? items : []) {
    const text = String(item?.str ?? '').trim();
    if (!text) continue;
    const x = Number(item?.x);
    const y = Number(item?.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    const width = positiveOrZero(item?.width);
    prepared.push({
      text,
      x,
      y,
      width,
      height: positiveOrZero(item?.height),
      centre: x + width / 2,
    });
  }
  return prepared;
}

/**
 * Group items into lines by `y`.
 *
 * Each line keeps the `y` of the first item that opened it as its anchor rather
 * than a running average: a drifting average lets a long run of slightly
 * descending items walk a whole line's worth of distance without ever tripping
 * the tolerance.
 */
function clusterLines(items, tolerance, yIncreasesDownward) {
  const sorted = items
    .slice()
    .sort((a, b) => (yIncreasesDownward ? a.y - b.y : b.y - a.y) || a.x - b.x);

  const lines = [];
  let current = null;
  for (const item of sorted) {
    if (!current || Math.abs(item.y - current.y) > tolerance) {
      current = { y: item.y, items: [item] };
      lines.push(current);
    } else {
      current.items.push(item);
    }
  }

  for (const line of lines) line.items.sort((a, b) => a.x - b.x);
  return lines;
}

/** The line naming the most days of the week, or -1 when none is convincing. */
function findHeaderLine(lines) {
  let bestIndex = -1;
  let bestScore = 0;
  lines.forEach((line, index) => {
    const text = line.items.map((item) => item.text).join(' ');
    const score = DAY_HEADER_PATTERNS.filter(({ re }) => re.test(text)).length;
    if (score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  });
  return bestScore >= MIN_HEADER_DAY_MATCHES ? bestIndex : -1;
}

/** Fallback column guide when no header row exists. */
function widestLine(lines) {
  return lines.reduce((best, line) => (line.items.length > (best?.items.length ?? 0) ? line : best), null);
}

/**
 * Combine every baseline of a visual header before deriving column centres.
 * A single-line label such as "Fri 31 Jul" is vertically centred between the
 * first and second lines of the other day labels, so looking only at the line
 * with the most weekday tokens silently loses Friday's entire column.
 */
function compositeHeaderLine(lines, headerLine, medianHeight) {
  const anchor = lines[headerLine];
  const radius = Math.max(
    (Number(medianHeight) || 0) * HEADER_REGION_HEIGHT_RATIO,
    MIN_ROW_TOLERANCE,
  );
  return {
    y: anchor.y,
    items: lines
      .filter((line) => Math.abs(line.y - anchor.y) <= radius)
      .flatMap((line) => line.items)
      .sort((left, right) => left.x - right.x),
  };
}

/**
 * Turn a header line into the boundaries between columns.
 *
 * Boundaries are the midpoints between consecutive header centres, which puts
 * the split halfway across the white space between two labels regardless of how
 * the cells beneath are aligned. The first gap is then mirrored to the left of
 * the first label to open the leading name column: names sit further left than
 * any dated header, and without that extra boundary every name would be filed
 * under Monday. When the header already has its own "Name" label the mirrored
 * boundary simply produces an empty column, which `rowsToRota` discards.
 */
function columnBoundaries(line, medianHeight) {
  if (!line?.items?.length) return [];
  const groups = mergeHeaderFragments(line.items, medianHeight);
  const centres = groups.map((group) => group.centre);

  const boundaries = [];
  for (let index = 0; index < centres.length - 1; index += 1) {
    boundaries.push((centres[index] + centres[index + 1]) / 2);
  }

  const leadingGap = boundaries.length
    ? boundaries[0] - centres[0]
    : Math.max(groups[0].width / 2, medianHeight * 3);
  boundaries.unshift(centres[0] - leadingGap);
  return boundaries;
}

/** Glue header items separated by less than one word space into one label. */
function mergeHeaderFragments(items, medianHeight) {
  const threshold = Math.max(medianHeight * HEADER_FRAGMENT_GAP_RATIO, MIN_ROW_TOLERANCE);
  const groups = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && item.x - (last.x + last.width) < threshold) {
      last.width = Math.max(last.x + last.width, item.x + item.width) - last.x;
      last.text = `${last.text} ${item.text}`;
    } else {
      groups.push({ x: item.x, width: item.width, text: item.text });
    }
  }
  return groups.map((group) => ({ ...group, centre: group.x + group.width / 2 }));
}

/** Drop every item of one line into a column and join what shares a cell. */
function lineToRow(line, boundaries) {
  const buckets = Array.from({ length: boundaries.length + 1 }, () => []);
  for (const item of line.items) {
    buckets[columnIndexFor(item.centre, boundaries)].push(item.text);
  }
  return {
    // Whitespace is collapsed here but dangling dashes are *not* stripped yet - 
    // "08:30 -" still needs its partner from the line below.
    cells: buckets.map((parts) => parts.join(' ').replace(/\s+/g, ' ').trim()),
    y: line.y,
    stitched: new Set(),
  };
}

function columnIndexFor(centre, boundaries) {
  let index = 0;
  while (index < boundaries.length && centre >= boundaries[index]) index += 1;
  return index;
}

/**
 * Discard leading columns nothing ever lands in.
 *
 * The mirrored name-column boundary is a guess, and when the header carries its
 * own "Name" label the guess opens a column to the left of everything. Removing
 * it here - rather than leaving it for `rowsToRota` - means column 0 is the name
 * column for the rest of this file, which the continuation rules depend on.
 */
function dropEmptyLeadingColumns(rows) {
  const width = rows.reduce((max, row) => Math.max(max, row.cells.length), 0);
  let leading = 0;
  while (leading < width && rows.every((row) => (row.cells[leading] ?? '') === '')) leading += 1;
  if (leading === 0 || leading >= width) return rows;
  for (const row of rows) row.cells = row.cells.slice(leading);
  return rows;
}

/**
 * Fold wrapped lines back into the table row they belong to.
 *
 * Two shapes occur, and they need different evidence:
 *
 *  * A line with no name but with cell content can only be the tail of the row
 *    above - nobody's shifts appear without them - so it merges unconditionally.
 *    This is what reunites "08:30 -" with "14:30".
 *  * A line that carries a name fragment is ambiguous: it is either the second
 *    half of a wrapped name or somebody rostered nowhere this week (which the
 *    source rota genuinely contains). Geometry decides, and *only* geometry - 
 *    the line may perfectly well carry cell fragments too, because a person
 *    whose name wraps usually has their times split across the very same pair of
 *    lines. A wrapped line sits at the tighter within-cell leading, well under
 *    the table's row pitch, so only a line that close to the one above is
 *    absorbed.
 *
 * The header row takes continuation lines like any other, because the header
 * label that wraps in the real document - "Total hours (Incl.breaks)" - has to
 * come back together or the column stops looking like a summary column.
 */
function mergeContinuationRows(rows, headerLine, medianHeight) {
  // Without a header there is no reliable point at which names become employee
  // anchors, so retain the conservative sequential behaviour used for generic
  // headerless PDFs.
  if (headerLine < 0) return mergeSequentialContinuations(rows, headerLine, medianHeight);

  const nameGroups = groupBodyNameRows(rows, headerLine, medianHeight);
  if (!nameGroups.length) return mergeSequentialContinuations(rows, headerLine, medianHeight);

  const clusters = [
    { indices: [headerLine], y: rows[headerLine].y },
    ...nameGroups,
  ];
  const fixedOwners = new Map([[headerLine, 0]]);
  nameGroups.forEach((group, groupIndex) => {
    for (const index of group.indices) fixedOwners.set(index, groupIndex + 1);
  });

  // A rota export centres a one-line name vertically in its table row. That
  // means the first line of "08:30 - / 14:30" can appear *before* the line
  // carrying the name. Assigning by the nearest name centre handles both that
  // shape and top-aligned exports, while midpoint boundaries keep adjacent rows
  // completely separate.
  for (let index = headerLine + 1; index < rows.length; index += 1) {
    const fixed = fixedOwners.get(index);
    const owner = fixed ?? nearestCluster(rows[index].y, clusters);
    if (!clusters[owner].indices.includes(index)) clusters[owner].indices.push(index);
  }

  const prefix = rows.slice(0, headerLine).map(cloneGridRow);
  const merged = clusters.map((cluster) => mergeGridRows(
    cluster.indices.map((index) => rows[index]),
    cluster.y,
  ));

  return { rows: [...prefix, ...merged], headerIndex: prefix.length };
}

/** Previous continuation strategy, retained for PDFs with no recognisable header. */
function mergeSequentialContinuations(rows, headerLine, medianHeight) {
  const pitch = medianPitch(rows, medianHeight);
  const merged = [];
  let headerIndex = -1;

  rows.forEach((row, index) => {
    const previous = merged[merged.length - 1];
    if (index !== headerLine && previous && isContinuation(row, previous, pitch)) {
      absorbRow(previous, row);
      return;
    }
    if (index === headerLine) headerIndex = merged.length;
    merged.push({ ...row, cells: row.cells.slice(), stitched: new Set() });
  });

  return { rows: merged, headerIndex };
}

/** Group one- and two-line employee names into stable vertical anchors. */
function groupBodyNameRows(rows, headerLine, medianHeight) {
  const named = [];
  for (let index = headerLine + 1; index < rows.length; index += 1) {
    if (rows[index].cells[0]) named.push(index);
  }

  const threshold = Math.max(
    (Number(medianHeight) || 0) * WRAPPED_NAME_GAP_HEIGHT_RATIO,
    MIN_ROW_TOLERANCE,
  );
  const groups = [];
  for (const index of named) {
    const previous = groups[groups.length - 1];
    const previousIndex = previous?.indices?.[previous.indices.length - 1];
    if (previous && Math.abs(rows[previousIndex].y - rows[index].y) <= threshold) {
      previous.indices.push(index);
      previous.y = average(previous.indices.map((itemIndex) => rows[itemIndex].y));
    } else {
      groups.push({ indices: [index], y: rows[index].y });
    }
  }
  return groups;
}

/** Index of the vertically closest header/name anchor. */
function nearestCluster(y, clusters) {
  let bestIndex = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  clusters.forEach((cluster, index) => {
    const distance = Math.abs(cluster.y - y);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });
  return bestIndex;
}

/** Merge all visual lines belonging to one table row in their page order. */
function mergeGridRows(rows, y) {
  const width = rows.reduce((maximum, row) => Math.max(maximum, row.cells.length), 0);
  const merged = { cells: Array.from({ length: width }, () => ''), y, stitched: new Set() };
  for (const row of rows) absorbRow(merged, row);
  return merged;
}

function cloneGridRow(row) {
  return { ...row, cells: row.cells.slice(), stitched: new Set(row.stitched) };
}

/**
 * Estimate the distance between one table row and the next.
 *
 * Filtering on "lines that carry a name" is not safe: a wrapped name carries one
 * too ("Caroline" / "Bentley"), so on the real document - where most names wrap - 
 * that filter measures the *leading* rather than the row pitch, halving the
 * estimate and making the wrap test reject the very wraps it exists to catch.
 *
 * Geometry is the reliable signal instead. Wrapped lines sit roughly one glyph
 * height below their partner; genuine rows sit a whole row apart. Discarding the
 * leading-sized gaps before taking the median keeps the estimate honest however
 * many names happen to wrap, and falls back to the raw gaps for a one-line-per-row
 * document where there is nothing to discard.
 */
function medianPitch(rows, medianHeight) {
  const gaps = [];
  for (let index = 1; index < rows.length; index += 1) {
    gaps.push(Math.abs(rows[index - 1].y - rows[index].y));
  }
  if (!gaps.length) return 0;

  const leading = Math.max(
    (Number(medianHeight) || 0) * WRAPPED_LEADING_HEIGHT_RATIO,
    MIN_ROW_TOLERANCE,
  );
  const rowGaps = gaps.filter((gap) => gap > leading);
  return median(rowGaps.length ? rowGaps : gaps);
}

function isContinuation(row, previous, pitch) {
  const hasName = row.cells[0] !== '';
  const hasContent = row.cells.slice(1).some((cell) => cell !== '');
  if (!hasName && !hasContent) return false;
  if (!hasName) return true;
  if (previous.cells[0] === '') return false;
  // A line carrying a name fragment is judged purely on geometry, whether or not
  // it also carries cell fragments. The source rota routinely wraps a name *and*
  // splits that person's times across the same pair of lines - "Caroline /
  // Bentley" sitting beside "09:00 - / 14:00". Requiring the continuation line
  // to be free of cell content invented a phantom employee called "Bentley" and
  // silently truncated Caroline's shift to "09:00".
  // Distance from where the row *started*, not from the last line absorbed into
  // it: after one wrap the remaining space to the next real row is small, and
  // measuring from the tail would read the next person as a third wrapped line.
  return pitch > 0 && Math.abs(previous.y - row.y) < pitch * WRAPPED_LINE_GAP_RATIO;
}

/** Append a continuation line's cells to the row above, remembering the joins. */
function absorbRow(target, row) {
  row.cells.forEach((cell, column) => {
    if (!cell) return;
    if (target.cells[column]) {
      const joined = `${target.cells[column]} ${cell}`;
      // A line ending in a dash followed by a clock time is the PDF's normal,
      // deterministic rendering of one shift. It does not need a warning. Any
      // other multi-line join remains marked for review.
      if (classifyShiftCell(joined).kind !== 'shift') target.stitched.add(column);
      target.cells[column] = joined;
    } else {
      target.cells[column] = cell;
    }
  });
}

/** Final pass: normalise whitespace and drop a dash that never found a partner. */
function tidyRow(row) {
  row.cells = row.cells.map((cell, column) => {
    const collapsed = cell.replace(/\s+/g, ' ').trim();
    const stripped = collapsed.replace(LEADING_DASH_RE, '').replace(TRAILING_DASH_RE, '');
    if (stripped !== collapsed) row.stitched.add(column);
    return stripped;
  });
}

/* -------------------------------------------------------------------------- */
/* Page assembly                                                              */
/* -------------------------------------------------------------------------- */

/** The header row of the first page that has one; null when none does. */
function firstHeaderCells(pageGrids) {
  for (const grid of pageGrids) {
    if (grid.headerIndex >= 0) return grid.rows[grid.headerIndex].cells.slice();
  }
  return null;
}

/**
 * Concatenate the body of every page.
 *
 * Page two of the real document repeats the whole header, and a repeated header
 * imported as data becomes a member of staff called "Mon 27 Jul", so both the
 * header each page detected for itself and any row identical to the first
 * header are dropped. Page numbers go the same way for the same reason.
 *
 * Anything above a page's header goes too: the table starts at its header, so a
 * title such as "Team rota - WC 27 July 2026" is a heading, not a person.
 */
function collectBodyRows(pageGrids, headerCells) {
  const headerSignature = headerCells ? rowSignature(headerCells) : null;
  const bodyRows = [];
  for (const grid of pageGrids) {
    grid.rows.forEach((row, index) => {
      if (grid.headerIndex >= 0 && index <= grid.headerIndex) return;
      if (row.cells.every((cell) => cell === '')) return;
      if (headerSignature && rowSignature(row.cells) === headerSignature) return;
      if (isPageFurniture(row.cells)) return;
      bodyRows.push(row);
    });
  }
  return bodyRows;
}

function rowSignature(cells) {
  return cells.map((cell) => cell.toLowerCase().replace(/\s+/g, ' ').trim()).join('|');
}

/** True for a lone "Page 2 of 3" footer sitting in an otherwise empty row. */
function isPageFurniture(cells) {
  const populated = cells.filter((cell) => cell !== '');
  return populated.length === 1 && PAGE_FURNITURE_RE.test(populated[0]);
}

/**
 * Line up each parsed rota row with the grid row it came from.
 *
 * `rowsToRota` preserves order and only ever drops rows, so equal lengths prove
 * a one-to-one mapping and the common case costs nothing. When it did drop
 * something (a row holding nothing but a weekly total, say) we fall back to
 * consuming grid rows by name, which keeps the pairing honest instead of
 * silently attributing one row's repairs to another.
 */
function alignSourceRows(rotaRows, bodyRows) {
  if (rotaRows.length === bodyRows.length) return bodyRows.slice();
  const remaining = bodyRows.slice();
  return rotaRows.map((rotaRow) => {
    const key = normaliseName(rotaRow.name);
    const index = remaining.findIndex((row) => normaliseName(row.cells[0] ?? '') === key);
    return index >= 0 ? remaining.splice(index, 1)[0] : null;
  });
}

/* -------------------------------------------------------------------------- */
/* Confidence                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Score one row and describe, in words a manager can act on, what is wrong.
 *
 * A row starts perfect and loses ground for the three things that actually go
 * wrong in a PDF import: no name in the first column, a cell that is neither a
 * shift nor an absence marker nor blank, and a cell we had to rebuild from two
 * lines. A stitched *name* is not penalised - two halves of a name can only go
 * back together one way - but a stitched time range is a genuine inference and
 * is both scored and reported.
 */
function scoreRotaRow({ rotaRow, sourceRow, rowIndex, headerCells, issues }) {
  const problems = [];
  let confidence = 1;
  // The name counts as one unit of evidence, each populated day as another, so
  // a row with six shifts weighs more in the document total than a row with one.
  let weight = 1;

  if (!rotaRow.name) {
    confidence -= PENALTIES.MISSING_NAME;
    problems.push('no name was found in the first column');
  }

  for (const dayKey of DAY_KEYS) {
    const value = rotaRow.cells?.[dayKey] ?? '';
    if (!value) continue;
    weight += 1;
    if (classifyShiftCell(value).kind === 'unparsed') {
      confidence -= PENALTIES.UNPARSED_CELL;
      problems.push(`${DAY_LABELS[dayKey]} reads "${value}", which is not a shift, an absence or blank`);
    }
  }

  for (const column of stitchedDayColumns(sourceRow, headerCells)) {
    confidence -= PENALTIES.STITCHED_CELL;
    const dayKey = dayKeyFromText(headerCells?.[column] ?? '');
    const label = dayKey ? DAY_LABELS[dayKey] : `Column ${column + 1}`;
    const value = sourceRow.cells[column];
    issues.push({
      row: rowIndex,
      column: dayKey,
      value,
      code: 'pdf-stitched-cell',
      message: `${label}: "${value}" was rebuilt from two lines of the PDF - check it reads correctly.`,
      severity: 'info',
    });
    problems.push(`${label} was rebuilt from two lines of the PDF`);
  }

  return { confidence: clamp01(confidence), weight, problems };
}

/**
 * Stitched columns that carry shifts. The name column is excluded because two
 * halves of a name can only go back together one way; the totals column because
 * `rowsToRota` throws it away anyway; and cells that ended up empty because
 * there is nothing there for the manager to check.
 */
function stitchedDayColumns(sourceRow, headerCells) {
  if (!sourceRow?.stitched?.size) return [];
  return [...sourceRow.stitched]
    .filter((column) => column > 0
      && sourceRow.cells[column] !== ''
      && !TOTALS_HEADER_RE.test(headerCells?.[column] ?? ''))
    .sort((a, b) => a - b);
}

/* -------------------------------------------------------------------------- */
/* Week commencing detection                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Find the week the document is describing, without ever acting on it.
 *
 * The heading is tried first because "WC 27 July 2026" states the week outright.
 * Failing that the day columns are used: "Mon 27 Jul" pins a date, and snapping
 * it back to its Monday gives the same answer from any column.
 */
/**
 * Work out which week a rota belongs to from its own text.
 *
 * Exported because a pasted rota needs exactly the same reading: it carries the
 * same "WC 3 August 2026" heading or the same dated day columns, and a rota
 * silently filed under the wrong week is worse than one that fails to import.
 * The paste importer has no geometry, so it passes its header row as `headerCells`.
 *
 * @param {string} documentText Everything readable in the document.
 * @param {string[]} headerCells The day-column headings, if known.
 * @param {string} hint The week currently on screen, used only to pick a year
 *   when the document gives a day and month but no year.
 * @returns {string|null} ISO Monday, or null when nothing was recognised.
 */
export function detectWeekCommencing(documentText, headerCells, hint) {
  const hintYear = parseIsoDate(hint)?.getUTCFullYear() ?? null;

  const heading = WEEK_COMMENCING_RE.exec(documentText ?? '');
  if (heading) {
    const iso = parseLooseDate(heading[0], { hintYear, weekdayIndex: 0 });
    if (iso) return mondayOf(iso);
  }

  for (const cell of headerCells ?? []) {
    const dayKey = dayKeyFromText(cell);
    if (!dayKey) continue;
    const iso = parseLooseDate(cell, { hintYear, weekdayIndex: DAY_KEYS.indexOf(dayKey) });
    if (iso) return mondayOf(iso);
  }

  return null;
}

/** Say what we found without changing anything, so the manager can decide. */
function weekCommencingWarnings(detected, weekCommencing) {
  if (!detected) return [];
  if (!weekCommencing) {
    return [`The PDF looks like the week commencing ${detected}. It has not been applied - set the week yourself before applying.`];
  }
  if (detected === weekCommencing) return [];
  return [
    `The PDF looks like the week commencing ${detected} but you are importing into ${weekCommencing}. `
      + 'Nothing has been changed - check which week you meant.',
  ];
}

/**
 * Pull a date out of loose prose: "27 July 2026", "27th Jul", "27/07/2026",
 * "Jul 27" or a bare ISO date.
 *
 * A missing year is resolved by asking which nearby year makes the date fall on
 * the weekday the text claims, which is exact for anything inside a few years
 * either side and far safer than assuming "this year".
 */
function parseLooseDate(text, { hintYear = null, weekdayIndex = null } = {}) {
  const lower = String(text ?? '').toLowerCase();

  const iso = /\b(\d{4})-(\d{2})-(\d{2})\b/.exec(lower);
  if (iso) return isoDate(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));

  const monthName = `(${MONTH_SHORT.join('|')})[a-z]*`;
  const dayFirst = new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?${monthName}\\.?,?\\s*(\\d{4}|\\d{2})?`).exec(lower);
  if (dayFirst) {
    return resolveDate(Number(dayFirst[1]), MONTH_SHORT.indexOf(dayFirst[2]), dayFirst[3], hintYear, weekdayIndex);
  }

  const monthFirst = new RegExp(`\\b${monthName}\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?,?\\s*(\\d{4}|\\d{2})?`).exec(lower);
  if (monthFirst) {
    return resolveDate(Number(monthFirst[2]), MONTH_SHORT.indexOf(monthFirst[1]), monthFirst[3], hintYear, weekdayIndex);
  }

  // Numeric dates last: "08.30" from a shift would happily masquerade as one, so
  // every candidate has to survive the month/day range check below.
  for (const match of lower.matchAll(/\b(\d{1,2})\s*[/\-.]\s*(\d{1,2})(?:\s*[/\-.]\s*(\d{2,4}))?\b/g)) {
    const day = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    if (monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) continue;
    const resolved = resolveDate(day, monthIndex, match[3], hintYear, weekdayIndex);
    if (resolved) return resolved;
  }

  return null;
}

function resolveDate(day, monthIndex, yearText, hintYear, weekdayIndex) {
  if (monthIndex < 0) return null;

  if (yearText) {
    const value = Number(yearText);
    const year = yearText.length === 2 ? 2000 + value : value;
    return isoDate(year, monthIndex, day);
  }

  const now = new Date().getUTCFullYear();
  const candidates = [hintYear, now, now + 1, now - 1, now + 2, now - 2].filter(
    (year, index, all) => Number.isFinite(year) && all.indexOf(year) === index,
  );
  for (const year of candidates) {
    const iso = isoDate(year, monthIndex, day);
    if (!iso) continue;
    if (weekdayIndex === null || weekdayIndexOf(iso) === weekdayIndex) return iso;
  }
  return null;
}

/** Build an ISO date, rejecting impossible ones such as 31 February. */
function isoDate(year, monthIndex, day) {
  if (!Number.isFinite(year) || monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) return null;
  const iso = `${String(year).padStart(4, '0')}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const parsed = parseIsoDate(iso);
  return parsed && parsed.getUTCDate() === day ? iso : null;
}

/** 0 = Monday, matching the index of DAY_KEYS. */
function weekdayIndexOf(iso) {
  const date = parseIsoDate(iso);
  return date ? (date.getUTCDay() + 6) % 7 : -1;
}

function dayKeyFromText(text) {
  const found = DAY_HEADER_PATTERNS.find(({ re }) => re.test(String(text ?? '')));
  return found ? found.dayKey : null;
}

/* -------------------------------------------------------------------------- */
/* pdf.js plumbing                                                            */
/* -------------------------------------------------------------------------- */

let pdfjsPromise = null;

/**
 * Import pdf.js once, lazily, and point it at its worker.
 *
 * The promise is cached so a manager importing three PDFs pays the download
 * once, but a *failed* load is not cached: a transient chunk-load error should
 * not permanently disable PDF import for the session.
 */
function loadPdfJs() {
  if (!pdfjsPromise) {
    pdfjsPromise = importPdfJs().catch((error) => {
      pdfjsPromise = null;
      throw error;
    });
  }
  return pdfjsPromise;
}

async function importPdfJs() {
  const module = await import('pdfjs-dist');
  // Named exports under Vite, a default namespace under some CJS interop.
  const pdfjs = typeof module?.getDocument === 'function'
    ? module
    : (typeof module?.default?.getDocument === 'function' ? module.default : null);
  if (!pdfjs) throw new Error('pdfjs-dist did not export getDocument()');
  await configureWorker(pdfjs);
  return pdfjs;
}

/**
 * Point pdf.js at the worker build Vite emitted for us.
 *
 * The `?url` import is dynamic rather than top-level so that importing this
 * module - which the pure grid tests do - never depends on a bundler being
 * present. If the URL cannot be resolved pdf.js falls back to parsing on the
 * main thread: slower, but a working import beats a broken one.
 */
async function configureWorker(pdfjs) {
  const options = pdfjs.GlobalWorkerOptions;
  if (!options || options.workerSrc) return;
  try {
    const workerModule = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
    const workerUrl = typeof workerModule === 'string' ? workerModule : workerModule?.default;
    if (typeof workerUrl === 'string' && workerUrl) options.workerSrc = workerUrl;
  } catch (error) {
    console.warn('Lunchly: the pdf.js worker could not be resolved; parsing on the main thread.', error);
  }
}

/**
 * Flatten a pdf.js text item into the geometry {@link textItemsToGrid} wants.
 * `transform` is `[scaleX, skewY, skewX, scaleY, x, y]`; `scaleY` stands in for
 * the height when the font gives us nothing usable.
 */
function toTextItem(item) {
  const transform = Array.isArray(item.transform) ? item.transform : [];
  return {
    str: item.str,
    x: Number(transform[4]) || 0,
    y: Number(transform[5]) || 0,
    width: positiveOrZero(item.width),
    height: positiveOrZero(item.height) || Math.abs(Number(transform[3]) || 0),
  };
}

/**
 * Accept whatever the UI hands us and hand pdf.js a private copy.
 * The copy matters twice: pdf.js takes ownership of the buffer it is given, and
 * nothing in Lunchly is allowed to mutate its caller's input.
 */
async function toPdfBytes(file) {
  if (!file) throw new TypeError('no file was supplied');
  if (file instanceof ArrayBuffer) return new Uint8Array(file.slice(0));
  if (ArrayBuffer.isView(file)) {
    return new Uint8Array(file.buffer.slice(file.byteOffset, file.byteOffset + file.byteLength));
  }
  if (typeof file.arrayBuffer === 'function') return new Uint8Array(await file.arrayBuffer());
  throw new TypeError('expected a PDF file, blob or array buffer');
}

/** Turn a pdf.js failure into something a manager can act on. */
function describePdfError(error) {
  switch (error?.name) {
    case 'PasswordException':
      return 'This PDF is password protected. Remove the password or export it again without one, then re-import.';
    case 'InvalidPDFException':
      return 'This file is not a readable PDF - it may be damaged or only partly downloaded. Try exporting it again.';
    case 'MissingPDFException':
      return 'The PDF could not be read back from disk. Try selecting the file again.';
    default:
      return `The PDF could not be read (${messageOf(error)}). Paste the rota as text instead.`;
  }
}

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The shape returned when nothing could be read. Confidence is zero and the
 * rota is empty but structurally valid, so the review table can render the
 * warning without any caller having to null-check its way through the result.
 */
function failureResult(weekCommencing, warning, extra = {}) {
  const { pages = 0, rawRows = [], detectedWeekCommencing = null, warnings = [], issues = [] } = extra;
  return {
    rota: rowsToRota([], { weekCommencing, source: 'pdf' }).rota,
    issues,
    confidence: 0,
    pages,
    rawRows,
    warnings: [...warnings, warning],
    detectedWeekCommencing,
  };
}

function messageOf(error) {
  return String(error?.message ?? error ?? 'unknown error');
}

function positiveOrZero(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
}

function average(values) {
  const numbers = values.filter((value) => Number.isFinite(value));
  if (!numbers.length) return 0;
  return numbers.reduce((sum, value) => sum + value, 0) / numbers.length;
}

function median(values) {
  const sorted = values.filter((value) => Number.isFinite(value) && value > 0).sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function clamp01(value) {
  return Math.min(1, Math.max(0, value));
}

function round3(value) {
  return Math.round(value * 1000) / 1000;
}
